import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import GlossaryTerm from '../Glossary/GlossaryTerm';

const Step6_Techniques = () => {
  const { dataA, updateDataRaw, nextStep, logDecision, completeWizard, switchWizard } = useWizard();
  const [techniques, setTechniques] = useState(dataA.techniques || {
    dataCollection: [],
    sampling: [],
    samplingDetails: {},
    sampleSizesPerMethod: {},
    analysis: ['descriptiveStats'],
    qualityCriteria: [],
    qualAnalysisJustification: '',
    quantAnalysisJustification: ''
  });
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (dataA.techniques) {
      const existingSampling = dataA.techniques.sampling;
      const samplingArray = Array.isArray(existingSampling) 
        ? existingSampling 
        : (existingSampling ? [existingSampling] : []);
      
      const existingAnalysis = dataA.techniques.analysis || [];
      const analysisWithDescriptive = existingAnalysis.includes('descriptiveStats') 
        ? existingAnalysis 
        : ['descriptiveStats', ...existingAnalysis];
      
      setTechniques({
        dataCollection: dataA.techniques.dataCollection || [],
        sampling: samplingArray,
        samplingDetails: dataA.techniques.samplingDetails || {},
        sampleSizesPerMethod: dataA.techniques.sampleSizesPerMethod || {},
        analysis: analysisWithDescriptive,
        qualityCriteria: dataA.techniques.qualityCriteria || [],
        qualAnalysisJustification: dataA.techniques.qualAnalysisJustification || '',
        quantAnalysisJustification: dataA.techniques.quantAnalysisJustification || ''
      });
    }
  }, [dataA.techniques]);

  const isQuantitative = dataA.methodologicalChoice === 'quantitative';
  const isQualitative = dataA.methodologicalChoice === 'qualitative';
  const isMixed = dataA.methodologicalChoice === 'mixedMethods';

  // Data Collection Methods
  const dataCollectionOptions = {
    quantitative: [
      { value: 'structuredSurvey', label: 'Structured Surveys/Questionnaires', description: 'Standardized instruments with fixed response options', glossaryKey: 'structuredSurvey' },
      { value: 'experiments', label: 'Experimental Procedures', description: 'Controlled manipulation and measurement', glossaryKey: 'experiments' },
      { value: 'structuredObs', label: 'Structured Observation', description: 'Systematic observation with coding scheme', glossaryKey: 'structuredObservation' },
      { value: 'secondaryData', label: 'Secondary/Administrative Data', description: 'Existing datasets and records', glossaryKey: 'secondaryData' },
      { value: 'tests', label: 'Standardized Tests/Scales', description: 'Validated measurement instruments', glossaryKey: 'standardizedTests' }
    ],
    qualitative: [
      { value: 'semiStructuredInt', label: 'Semi-Structured Interviews', description: 'Flexible interviews with guide', glossaryKey: 'semiStructuredInterview' },
      { value: 'structuredInt', label: 'Structured Interviews', description: 'Fixed questions asked consistently', glossaryKey: 'structuredInterview' },
      { value: 'focusGroups', label: 'Focus Groups', description: 'Group discussions (6-10 participants)', glossaryKey: 'focusGroup' },
      { value: 'participantObs', label: 'Participant Observation', description: 'Immersive observation while participating', glossaryKey: 'participantObservation' },
      { value: 'documents', label: 'Document Analysis', description: 'Analyzing texts, records, artifacts', glossaryKey: 'documentAnalysis' },
      { value: 'fieldNotes', label: 'Field Notes/Journals', description: 'Researcher\'s reflective records', glossaryKey: 'fieldNotes' },
      { value: 'visualMethods', label: 'Visual Methods', description: 'Photos, videos, drawings, diagrams', glossaryKey: 'visualMethods' }
    ]
  };

  // Sampling Strategies - Three categories with max 4 selections
  const samplingOptions = {
    probability: [
      { value: 'simpleRandom', label: 'Simple Random Sampling', description: 'Every individual has equal chance of selection' },
      { value: 'systematic', label: 'Systematic Sampling', description: 'Select every kth element after a random start' },
      { value: 'stratified', label: 'Stratified Sampling', description: 'Population divided into subgroups and sampled proportionally' },
      { value: 'cluster', label: 'Cluster Sampling', description: 'Population divided into clusters and whole clusters are sampled' },
      { value: 'multistage', label: 'Multistage Sampling', description: 'Combination of cluster + random sampling at different stages' },
      { value: 'pps', label: 'Probability Proportional to Size (PPS)', description: 'Larger clusters have higher probability of selection' }
    ],
    nonProbability: [
      { value: 'convenience', label: 'Convenience Sampling', description: 'Selecting participants that are easiest to access', glossaryKey: 'convenienceSampling' },
      { value: 'purposive', label: 'Purposive (Judgmental) Sampling', description: 'Selecting participants with specific expertise or characteristics', glossaryKey: 'purposiveSampling' },
      { value: 'quota', label: 'Quota Sampling', description: 'Fixing quotas for subgroups but sampling non-randomly' },
      { value: 'snowball', label: 'Snowball Sampling', description: 'Participants refer others to the study', glossaryKey: 'snowballSampling' },
      { value: 'volunteer', label: 'Volunteer / Self-Selection Sampling', description: 'People choose to participate' },
      { value: 'expert', label: 'Expert Sampling', description: 'Selecting recognized experts in a field' },
      { value: 'theoretical', label: 'Theoretical Sampling', description: 'Used in grounded theory; sampling evolves during the study', glossaryKey: 'theoreticalSampling' },
      { value: 'homogeneous', label: 'Homogeneous Sampling', description: 'All participants share similar characteristics' },
      { value: 'maxVariation', label: 'Maximum Variation Sampling', description: 'Capturing wide diversity of experiences' },
      { value: 'criticalCase', label: 'Critical Case Sampling', description: 'Selecting cases with special significance' }
    ],
    advanced: [
      { value: 'sequential', label: 'Sequential Sampling', description: 'Sample selection occurs in phases based on earlier results' },
      { value: 'adaptive', label: 'Adaptive Sampling', description: 'Sample design changes when rare cases are found' },
      { value: 'rds', label: 'Respondent-Driven Sampling (RDS)', description: 'Advanced form of snowball sampling with statistical weighting' },
      { value: 'panel', label: 'Panel Sampling', description: 'Same sample studied repeatedly over time' }
    ]
  };
  
  const MAX_SAMPLING_SELECTIONS = 4;

  // Analysis Methods
  const analysisOptions = {
    quantitative: [
      { value: 'descriptiveStats', label: 'Descriptive Statistics', description: 'Means, frequencies, distributions', glossaryKey: 'descriptiveStatistics' },
      { value: 'inferentialStats', label: 'Inferential Statistics', description: 'Hypothesis testing, significance tests', glossaryKey: 'inferentialStatistics' },
      { value: 'regression', label: 'Regression Analysis', description: 'Linear, logistic, multilevel regression', glossaryKey: 'regression' },
      { value: 'anova', label: 'ANOVA/ANCOVA', description: 'Comparing group means', glossaryKey: 'anova' },
      { value: 'factorAnalysis', label: 'Factor Analysis', description: 'Exploratory or confirmatory', glossaryKey: 'factorAnalysis' },
      { value: 'sem', label: 'Structural Equation Modeling', description: 'Latent variables and path analysis', glossaryKey: 'sem' },
      { value: 'timeSeries', label: 'Time Series Analysis', description: 'Longitudinal data analysis', glossaryKey: 'timeSeries' }
    ],
    qualitative: [
      { value: 'thematicAnalysis', label: 'Thematic Analysis', description: 'Identify and analyze patterns/themes', glossaryKey: 'thematicAnalysis' },
      { value: 'contentAnalysis', label: 'Content Analysis', description: 'Systematic coding of content', glossaryKey: 'contentAnalysis' },
      { value: 'sentimentAnalysis', label: 'Sentiment Analysis', description: 'Analyze emotional tone and polarity of text', glossaryKey: 'sentimentAnalysis' },
      { value: 'groundedTheoryCoding', label: 'Grounded Theory Coding', description: 'Open, axial, selective coding', glossaryKey: 'groundedTheoryCoding' },
      { value: 'narrativeAnalysis', label: 'Narrative Analysis', description: 'Story structure and meaning', glossaryKey: 'narrativeAnalysis' },
      { value: 'discourseAnalysis', label: 'Discourse Analysis', description: 'Language use and power', glossaryKey: 'discourseAnalysis' },
      { value: 'phenomenologicalAnalysis', label: 'Phenomenological Analysis', description: 'Essence of lived experience', glossaryKey: 'phenomenologicalAnalysis' },
      { value: 'frameworkAnalysis', label: 'Framework Analysis', description: 'Matrix-based analysis', glossaryKey: 'frameworkAnalysis' },
      { value: 'ipa', label: 'Interpretative Phenomenological Analysis (IPA)', description: 'Individual meaning-making', glossaryKey: 'ipa' }
    ]
  };

  // Quality Criteria
  const qualityCriteriaOptions = {
    quantitative: [
      { value: 'internalValidity', label: 'Internal Validity', description: 'Can we infer causality?', glossaryKey: 'internalValidity' },
      { value: 'externalValidity', label: 'External Validity', description: 'Can we generalize?', glossaryKey: 'externalValidity' },
      { value: 'reliability', label: 'Reliability', description: 'Consistency of measurement', glossaryKey: 'reliability' },
      { value: 'constructValidity', label: 'Construct Validity', description: 'Measuring what we intend?', glossaryKey: 'constructValidity' },
      { value: 'statConclusion', label: 'Statistical Conclusion Validity', description: 'Appropriate statistical inferences', glossaryKey: 'statConclusionValidity' }
    ],
    qualitative: [
      { value: 'credibility', label: 'Credibility', description: 'Truth value - are findings believable?', glossaryKey: 'credibility' },
      { value: 'transferability', label: 'Transferability', description: 'Can findings apply elsewhere?', glossaryKey: 'transferability' },
      { value: 'dependability', label: 'Dependability', description: 'Would findings be consistent if replicated?', glossaryKey: 'dependability' },
      { value: 'confirmability', label: 'Confirmability', description: 'Are findings shaped by participants, not bias?', glossaryKey: 'confirmability' },
      { value: 'authenticity', label: 'Authenticity', description: 'Fair representation of all perspectives?', glossaryKey: 'authenticity' },
      { value: 'resonance', label: 'Resonance', description: 'Do findings ring true to participants?', glossaryKey: 'resonance' },
      { value: 'triangulation', label: 'Triangulation', description: 'Multiple methods/sources', glossaryKey: 'triangulation' }
    ]
  };

  const getRelevantOptions = (optionSet) => {
    if (isMixed) {
      return [...(optionSet.quantitative || []), ...(optionSet.qualitative || [])];
    }
    if (isQuantitative) return optionSet.quantitative || optionSet.probability || [];
    if (isQualitative) return optionSet.qualitative || optionSet.nonProbability || [];
    // If no methodology selected, show all options
    return [...(optionSet.quantitative || []), ...(optionSet.qualitative || []), ...(optionSet.probability || []), ...(optionSet.nonProbability || [])];
  };

  // Helper functions to count QUAL vs QUANT analysis methods
  const getSelectedQualAnalysisMethods = () => {
    const qualMethods = analysisOptions.qualitative.map(o => o.value);
    return techniques.analysis.filter(method => qualMethods.includes(method));
  };

  const getSelectedQuantAnalysisMethods = () => {
    const quantMethods = analysisOptions.quantitative.map(o => o.value);
    return techniques.analysis.filter(method => quantMethods.includes(method) && method !== 'descriptiveStats');
  };

  const qualAnalysisCount = getSelectedQualAnalysisMethods().length;
  const quantAnalysisCount = getSelectedQuantAnalysisMethods().length;
  const needsQualJustification = qualAnalysisCount > 3;
  const needsQuantJustification = quantAnalysisCount > 3;

  const getSamplingArray = () => {
    if (Array.isArray(techniques.sampling)) return techniques.sampling;
    if (techniques.sampling) return [techniques.sampling];
    return [];
  };
  
  const samplingArray = getSamplingArray();

  // Validation
  useEffect(() => {
    const newWarnings = [];
    const currentSampling = Array.isArray(techniques.sampling) ? techniques.sampling : (techniques.sampling ? [techniques.sampling] : []);

    // Data collection minimum
    if (techniques.dataCollection.length === 0) {
      newWarnings.push({
        type: 'error',
        message: 'Select at least one data collection method.'
      });
    }

    // Sampling required
    if (currentSampling.length === 0) {
      newWarnings.push({
        type: 'error',
        message: 'Select at least one sampling strategy (maximum 4).'
      });
    }

    // Sample size validation - check per-method sample sizes
    if (techniques.dataCollection.length > 0) {
      const hasSampleSizes = techniques.dataCollection.some(
        method => techniques.sampleSizesPerMethod[method] && techniques.sampleSizesPerMethod[method] > 0
      );
      if (!hasSampleSizes) {
        newWarnings.push({
          type: 'warning',
          message: 'Specify sample sizes for your data collection methods above.'
        });
      }
    }

    // Probability sampling generalizability
    const hasProbabilitySampling = currentSampling.some(s => 
      samplingOptions.probability.find(opt => opt.value === s)
    );
    if (hasProbabilitySampling && dataA.intake?.generalisability === 'no') {
      newWarnings.push({
        type: 'warning',
        message: 'You selected probability sampling but indicated generalizability is not important. Consider if simpler non-probability sampling would suffice.'
      });
    }

    // Convenience sampling warning
    if (currentSampling.includes('convenience') && dataA.intake?.generalisability === 'yes') {
      newWarnings.push({
        type: 'warning',
        message: 'Convenience sampling limits generalizability. You indicated generalization is important - consider random sampling if feasible.'
      });
    }

    // Theoretical sampling alignment
    if (currentSampling.includes('theoretical') && dataA.strategy !== 'groundedTheory') {
      newWarnings.push({
        type: 'warning',
        message: 'Theoretical sampling is specific to Grounded Theory methodology. For other qualitative approaches, consider purposive sampling.'
      });
    }

    // Analysis methods minimum
    if (techniques.analysis.length === 0) {
      newWarnings.push({
        type: 'error',
        message: 'Select at least one analysis method.'
      });
    }

    // Strategy-analysis alignment
    if (dataA.strategy === 'groundedTheory' && !techniques.analysis.includes('groundedTheoryCoding')) {
      newWarnings.push({
        type: 'warning',
        message: 'Grounded Theory strategy should use grounded theory coding methods (open, axial, selective coding).'
      });
    }

    if (dataA.strategy === 'phenomenology' && !techniques.analysis.includes('phenomenologicalAnalysis')) {
      newWarnings.push({
        type: 'warning',
        message: 'Phenomenological research typically requires phenomenological analysis methods (e.g., Moustakas, van Manen, IPA).'
      });
    }

    // Quality criteria minimum
    if (techniques.qualityCriteria.length === 0) {
      newWarnings.push({
        type: 'warning',
        message: 'Select quality criteria you will address. This demonstrates rigor and is essential for peer review.'
      });
    }

    // Analysis methods guardrails - warn if >3 QUAL or >3 QUANT methods
    // Note: descriptiveStats is always selected by default and doesn't count towards the limit
    const selectedQualMethods = analysisOptions.qualitative.map(o => o.value).filter(v => techniques.analysis.includes(v));
    const selectedQuantMethods = analysisOptions.quantitative.map(o => o.value)
      .filter(v => techniques.analysis.includes(v))
      .filter(v => v !== 'descriptiveStats');

    if (selectedQualMethods.length > 3) {
      newWarnings.push({
        type: 'warning',
        message: `You've selected ${selectedQualMethods.length} qualitative analysis methods. Most studies use 1-3 methods. Please provide a justification below.`
      });
      if (!techniques.qualAnalysisJustification || techniques.qualAnalysisJustification.trim().length < 10) {
        newWarnings.push({
          type: 'error',
          message: 'Justification required: Explain why you need more than 3 qualitative analysis methods.'
        });
      }
    }

    if (selectedQuantMethods.length > 3) {
      newWarnings.push({
        type: 'warning',
        message: `You've selected ${selectedQuantMethods.length} quantitative analysis methods. Most studies use 1-3 methods. Please provide a justification below.`
      });
      if (!techniques.quantAnalysisJustification || techniques.quantAnalysisJustification.trim().length < 10) {
        newWarnings.push({
          type: 'error',
          message: 'Justification required: Explain why you need more than 3 quantitative analysis methods.'
        });
      }
    }

    // Mixed methods integration check
    if (isMixed) {
      const hasQuant = techniques.dataCollection.some(dc => 
        dataCollectionOptions.quantitative.find(opt => opt.value === dc)
      );
      const hasQual = techniques.dataCollection.some(dc => 
        dataCollectionOptions.qualitative.find(opt => opt.value === dc)
      );

      if (!hasQuant || !hasQual) {
        newWarnings.push({
          type: 'error',
          message: 'Mixed methods design requires both quantitative AND qualitative data collection methods.'
        });
      }
    }

    setWarnings(newWarnings);
  }, [techniques, dataA, isQuantitative, isQualitative, isMixed]);

  const handleCollectionToggle = (value) => {
    setTechniques(prev => ({
      ...prev,
      dataCollection: prev.dataCollection.includes(value)
        ? prev.dataCollection.filter(v => v !== value)
        : [...prev.dataCollection, value]
    }));
  };

  const handleAnalysisToggle = (value) => {
    if (value === 'descriptiveStats') {
      return;
    }
    setTechniques(prev => ({
      ...prev,
      analysis: prev.analysis.includes(value)
        ? prev.analysis.filter(v => v !== value)
        : [...prev.analysis, value]
    }));
  };

  const handleQualityToggle = (value) => {
    setTechniques(prev => ({
      ...prev,
      qualityCriteria: prev.qualityCriteria.includes(value)
        ? prev.qualityCriteria.filter(v => v !== value)
        : [...prev.qualityCriteria, value]
    }));
  };

  const handleSamplingToggle = (value) => {
    setTechniques(prev => {
      const currentSampling = Array.isArray(prev.sampling) ? prev.sampling : (prev.sampling ? [prev.sampling] : []);
      if (currentSampling.includes(value)) {
        return { ...prev, sampling: currentSampling.filter(v => v !== value) };
      } else if (currentSampling.length < MAX_SAMPLING_SELECTIONS) {
        return { ...prev, sampling: [...currentSampling, value] };
      }
      return prev;
    });
  };
  
  const getSamplingLabel = (value) => {
    const allOptions = [...samplingOptions.probability, ...samplingOptions.nonProbability, ...samplingOptions.advanced];
    const option = allOptions.find(o => o.value === value);
    return option?.label || value;
  };

  const handleDetailsChange = (key, value) => {
    setTechniques(prev => ({
      ...prev,
      samplingDetails: { ...prev.samplingDetails, [key]: value }
    }));
  };

  const handleJustificationChange = (type, value) => {
    setTechniques(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleMethodSampleSizeChange = (methodValue, size) => {
    setTechniques(prev => ({
      ...prev,
      sampleSizesPerMethod: { 
        ...prev.sampleSizesPerMethod, 
        [methodValue]: parseInt(size) || 0 
      }
    }));
  };

  const getMethodLabel = (methodValue) => {
    const allOptions = [...(dataCollectionOptions.quantitative || []), ...(dataCollectionOptions.qualitative || [])];
    const option = allOptions.find(o => o.value === methodValue);
    return option?.label || methodValue;
  };

  const isQuantitativeMethod = (methodValue) => {
    return dataCollectionOptions.quantitative?.some(o => o.value === methodValue);
  };

  const canProceed = () => {
    const hasBlockingErrors = warnings.some(w => w.type === 'error');
    return !hasBlockingErrors;
  };

  const handleComplete = () => {
    const finalSampling = Array.isArray(techniques.sampling) ? techniques.sampling : (techniques.sampling ? [techniques.sampling] : []);
    
    updateDataRaw({
      'wizardA.techniques': { ...techniques, sampling: finalSampling }
    });

    logDecision(
      'Techniques & Procedures',
      'Complete',
      `Data collection: ${techniques.dataCollection.join(', ')}. Sampling: ${finalSampling.join(', ')}. Analysis: ${techniques.analysis.join(', ')}. Quality: ${techniques.qualityCriteria.join(', ')}.`
    );

    completeWizard('A');
    
    // If quantitative or mixed, prompt to continue to Wizard B
    if (isQuantitative || isMixed) {
      const proceedToB = window.confirm(
        'Wizard A Complete! 🎉\n\n' +
        'Since you are using quantitative methods, would you like to proceed to Wizard B (Statistical Analysis Planning)?'
      );
      if (proceedToB) {
        switchWizard('B');
      } else {
        nextStep(); // Go to final report
      }
    } else {
      nextStep(); // Go to final report
    }
  };

  return (
    <div className="wizard-step">
      <h2>Step 8: Techniques & Procedures</h2>
      <p className="step-description">
        Define the specific techniques you'll use for data collection, sampling, analysis, and ensuring quality.
      </p>

      <ExplanationBox title="Final Layer: Operationalizing Your Design">
        <p>
          This is where your methodology becomes actionable. You'll specify exactly HOW you'll:
        </p>
        <ul>
          <li>Collect data (instruments and procedures)</li>
          <li>Select participants (sampling strategy)</li>
          <li>Analyze data (analytical techniques)</li>
          <li>Ensure quality and rigor (quality criteria)</li>
        </ul>
      </ExplanationBox>

      {/* DATA COLLECTION */}
      <div className="form-section">
        <h3>1. Data Collection Methods</h3>
        <p className="helper-text">
          Select all methods you will use (multiple selections encouraged for {isMixed ? 'mixed methods' : 'triangulation'}):
        </p>

        <div className="checkbox-grid">
          {getRelevantOptions(dataCollectionOptions).map(option => (
            <label key={option.value} className="checkbox-card">
              <input
                type="checkbox"
                checked={techniques.dataCollection.includes(option.value)}
                onChange={() => handleCollectionToggle(option.value)}
              />
              <div className="checkbox-content">
                <strong>{option.label}</strong>
                {option.glossaryKey && <GlossaryTerm termKey={option.glossaryKey} />}
                <p>{option.description}</p>
              </div>
            </label>
          ))}
        </div>

        {techniques.dataCollection.length > 0 && (
          <div className="per-method-sample-sizes">
            <h4>Sample Size Per Method</h4>
            <p className="helper-text">
              Specify the expected sample size for each data collection method you selected:
            </p>
            <div className="sample-size-grid">
              {techniques.dataCollection.map(methodValue => (
                <div key={methodValue} className="sample-size-row">
                  <label>
                    <span className={`method-type-badge ${isQuantitativeMethod(methodValue) ? 'quant' : 'qual'}`}>
                      {isQuantitativeMethod(methodValue) ? 'QUANT' : 'QUAL'}
                    </span>
                    {getMethodLabel(methodValue)}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={techniques.sampleSizesPerMethod[methodValue] || ''}
                    onChange={(e) => handleMethodSampleSizeChange(methodValue, e.target.value)}
                    placeholder={isQuantitativeMethod(methodValue) ? "e.g., 399" : "e.g., 15-20"}
                  />
                  <small>
                    {isQuantitativeMethod(methodValue) 
                      ? 'N for statistical analysis' 
                      : 'Participants until saturation'}
                  </small>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* SAMPLING */}
      <div className="form-section">
        <h3>2. Sampling Strategy <GlossaryTerm termKey="probabilitySampling" /></h3>
        <p className="helper-text">
          Select up to {MAX_SAMPLING_SELECTIONS} sampling strategies that you will use:
          <span className={`selection-counter ${samplingArray.length >= MAX_SAMPLING_SELECTIONS ? 'max-reached' : ''}`}>
            {' '}({samplingArray.length}/{MAX_SAMPLING_SELECTIONS} selected)
          </span>
        </p>

        {/* Selected sampling strategies summary */}
        {samplingArray.length > 0 && (
          <div className="selected-items-summary">
            <strong>Selected:</strong> {samplingArray.map(s => getSamplingLabel(s)).join(', ')}
          </div>
        )}
        
        <div className="sampling-category">
          <h4>Probability Sampling</h4>
          <p className="category-description">Every unit has a known, non-zero chance of being selected - used in quantitative and statistical studies</p>
          <div className="checkbox-grid">
            {samplingOptions.probability.map(option => (
              <label key={option.value} className={`checkbox-card ${samplingArray.length >= MAX_SAMPLING_SELECTIONS && !samplingArray.includes(option.value) ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={samplingArray.includes(option.value)}
                  onChange={() => handleSamplingToggle(option.value)}
                  disabled={samplingArray.length >= MAX_SAMPLING_SELECTIONS && !samplingArray.includes(option.value)}
                />
                <div className="checkbox-content">
                  <strong>{option.label}</strong>
                  {option.glossaryKey && <GlossaryTerm termKey={option.glossaryKey} />}
                  <p>{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="sampling-category">
          <h4>Non-Probability Sampling</h4>
          <p className="category-description">Used when probability sampling is impractical - common in qualitative and exploratory research</p>
          <div className="checkbox-grid">
            {samplingOptions.nonProbability.map(option => (
              <label key={option.value} className={`checkbox-card ${samplingArray.length >= MAX_SAMPLING_SELECTIONS && !samplingArray.includes(option.value) ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={samplingArray.includes(option.value)}
                  onChange={() => handleSamplingToggle(option.value)}
                  disabled={samplingArray.length >= MAX_SAMPLING_SELECTIONS && !samplingArray.includes(option.value)}
                />
                <div className="checkbox-content">
                  <strong>{option.label}</strong>
                  {option.glossaryKey && <GlossaryTerm termKey={option.glossaryKey} />}
                  <p>{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="sampling-category">
          <h4>Mixed & Advanced Techniques</h4>
          <p className="category-description">To capture complexity, improve accuracy, adapt to evolving research conditions</p>
          <div className="checkbox-grid">
            {samplingOptions.advanced.map(option => (
              <label key={option.value} className={`checkbox-card ${samplingArray.length >= MAX_SAMPLING_SELECTIONS && !samplingArray.includes(option.value) ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={samplingArray.includes(option.value)}
                  onChange={() => handleSamplingToggle(option.value)}
                  disabled={samplingArray.length >= MAX_SAMPLING_SELECTIONS && !samplingArray.includes(option.value)}
                />
                <div className="checkbox-content">
                  <strong>{option.label}</strong>
                  {option.glossaryKey && <GlossaryTerm termKey={option.glossaryKey} />}
                  <p>{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Sampling Frame / Recruitment Plan</label>
          <textarea
            value={techniques.samplingDetails.recruitmentPlan || ''}
            onChange={(e) => handleDetailsChange('recruitmentPlan', e.target.value)}
            placeholder="Describe your sampling frame and how you will recruit participants..."
            rows="3"
          />
        </div>
      </div>

      {/* ANALYSIS */}
      <div className="form-section">
        <h3>3. Data Analysis Methods</h3>
        <p className="helper-text">
          Select all analysis techniques you plan to use:
        </p>

        <div className="checkbox-grid">
          {getRelevantOptions(analysisOptions).map(option => (
            <label 
              key={option.value} 
              className={`checkbox-card ${option.value === 'descriptiveStats' ? 'locked' : ''}`}
              title={option.value === 'descriptiveStats' ? 'Required for all research designs' : ''}
            >
              <input
                type="checkbox"
                checked={techniques.analysis.includes(option.value)}
                onChange={() => handleAnalysisToggle(option.value)}
                disabled={option.value === 'descriptiveStats'}
              />
              <div className="checkbox-content">
                <strong>{option.label}</strong>
                {option.value === 'descriptiveStats' && <span className="required-badge">Required</span>}
                {option.glossaryKey && <GlossaryTerm termKey={option.glossaryKey} />}
                <p>{option.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Analysis method count indicators */}
        {(qualAnalysisCount > 0 || quantAnalysisCount > 0) && (
          <div className="analysis-count-summary">
            {qualAnalysisCount > 0 && (
              <span className={`count-badge qual ${qualAnalysisCount > 3 ? 'warning' : ''}`}>
                QUAL: {qualAnalysisCount} selected {qualAnalysisCount > 3 && '⚠️'}
              </span>
            )}
            {quantAnalysisCount > 0 && (
              <span className={`count-badge quant ${quantAnalysisCount > 3 ? 'warning' : ''}`}>
                QUANT: {quantAnalysisCount} selected {quantAnalysisCount > 3 && '⚠️'}
              </span>
            )}
          </div>
        )}

        {/* Justification for >3 QUAL methods */}
        {needsQualJustification && (
          <div className="justification-box qual">
            <div className="justification-header">
              <span className="justification-icon">📝</span>
              <strong>Justification Required: Qualitative Methods</strong>
            </div>
            <p className="justification-info">
              You've selected {qualAnalysisCount} qualitative analysis methods. Most studies use 1-3 methods. 
              Please explain why your study requires multiple qualitative approaches.
            </p>
            <textarea
              value={techniques.qualAnalysisJustification || ''}
              onChange={(e) => handleJustificationChange('qualAnalysisJustification', e.target.value)}
              placeholder="e.g., Grounded Theory requires iterative coding passes (open, axial, selective), or I'm using triangulation across multiple analytical lenses..."
              rows="3"
            />
          </div>
        )}

        {/* Justification for >3 QUANT methods */}
        {needsQuantJustification && (
          <div className="justification-box quant">
            <div className="justification-header">
              <span className="justification-icon">📝</span>
              <strong>Justification Required: Quantitative Methods</strong>
            </div>
            <p className="justification-info">
              You've selected {quantAnalysisCount} quantitative analysis methods. Most studies use 1-3 methods. 
              Please explain why your study requires multiple quantitative approaches.
            </p>
            <textarea
              value={techniques.quantAnalysisJustification || ''}
              onChange={(e) => handleJustificationChange('quantAnalysisJustification', e.target.value)}
              placeholder="e.g., My analysis plan involves descriptive stats first, then factor analysis to validate the scale, followed by regression to test hypotheses..."
              rows="3"
            />
          </div>
        )}
      </div>

      {/* QUALITY CRITERIA */}
      <div className="form-section">
        <h3>4. Quality Criteria & Rigor</h3>
        <p className="helper-text">
          Select the quality criteria you will address in your study:
        </p>

        <div className="checkbox-grid">
          {getRelevantOptions(qualityCriteriaOptions).map(option => (
            <label key={option.value} className="checkbox-card">
              <input
                type="checkbox"
                checked={techniques.qualityCriteria.includes(option.value)}
                onChange={() => handleQualityToggle(option.value)}
              />
              <div className="checkbox-content">
                <strong>{option.label}</strong>
                {option.glossaryKey && <GlossaryTerm termKey={option.glossaryKey} />}
                <p>{option.description}</p>
              </div>
            </label>
          ))}
        </div>

        {isQualitative && (
          <ExplanationBox title="Establishing Rigor in Qualitative Research">
            <p>Recommended strategies:</p>
            <ul>
              <li><strong>Credibility:</strong> Prolonged engagement, member checking, triangulation</li>
              <li><strong>Transferability:</strong> Thick description of context, participants, processes</li>
              <li><strong>Dependability:</strong> Audit trail, reflexive journal, clear documentation</li>
              <li><strong>Confirmability:</strong> Reflexivity, peer debriefing, data-driven interpretations</li>
            </ul>
            <p>Consider citing: Guba & Lincoln (1989), Tracy (2010), or Lincoln & Guba (1985)</p>
          </ExplanationBox>
        )}

        {isQuantitative && (
          <ExplanationBox title="Ensuring Rigor in Quantitative Research">
            <p>Key considerations:</p>
            <ul>
              <li><strong>Internal Validity:</strong> Control confounds, random assignment, blinding</li>
              <li><strong>External Validity:</strong> Representative sampling, replication across settings</li>
              <li><strong>Reliability:</strong> Test-retest, internal consistency (Cronbach's α), inter-rater reliability</li>
              <li><strong>Construct Validity:</strong> Use validated instruments, convergent/discriminant validity</li>
            </ul>
            <p>Consider citing: Cook & Campbell (1979), Shadish et al. (2002)</p>
          </ExplanationBox>
        )}
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="warnings-section">
          {warnings.map((warning, idx) => (
            <GuardrailWarning 
              key={idx} 
              type={warning.type}
              title={warning.type === 'error' ? 'Required' : 'Recommendation'}
            >
              {warning.message}
            </GuardrailWarning>
          ))}
        </div>
      )}

      <NavigationButtons
        onNext={handleComplete}
        canProceed={true}
        nextLabel="Complete Wizard A"
        isLastStep={true}
      />
    </div>
  );
};

export default Step6_Techniques;
